import { isGameAdUuid } from './ids';
import type { GameAdFormat, GameAdPlacement, GameAdReason, GameAdOutcomeStatus } from './types';

const GAME_AD_TEST_FRAME_PATH = '/game-ad-test-frame.html';
const FRAME_LOAD_TIMEOUT_MS = 8_000;
const PLACEMENT_TIMEOUT_MS = 30_000;
const TEST_CHANNEL = 'atrivial-game-ad-test-v1';

export type SdkTerminalStatus = Extract<
  GameAdOutcomeStatus,
  'completed' | 'dismissed' | 'no_fill' | 'failed' | 'timeout' | 'cancelled'
>;

export type GameAdSdkResult = Readonly<{
  status: SdkTerminalStatus;
  elapsedMs: number;
  reason?: GameAdReason;
}>;

export type GameAdSdkRequest = Readonly<{
  requestId: string;
  format: Exclude<GameAdFormat, 'none' | 'banner'>;
  placement: GameAdPlacement;
  signal: AbortSignal;
  onShown: () => void;
}>;

export interface GameAdSdk {
  request(input: GameAdSdkRequest): Promise<GameAdSdkResult>;
}

export class GameAdSdkError extends Error {
  constructor(
    message: string,
    readonly code: 'load_failed' | 'placement_failed'
  ) {
    super(message);
    this.name = 'GameAdSdkError';
  }
}

type GoogleBreakStatus =
  | 'notReady'
  | 'timeout'
  | 'invalid'
  | 'error'
  | 'noAdPreloaded'
  | 'frequencyCapped'
  | 'ignored'
  | 'other'
  | 'dismissed'
  | 'viewed';

type TestFrameMessageType =
  | 'ready'
  | 'prompt'
  | 'failed'
  | 'cancel'
  | 'shown'
  | 'reward_viewed'
  | 'reward_dismissed'
  | 'after_ad'
  | 'break_done';

type TestFrameMessage = Readonly<{
  channel?: unknown;
  requestId?: unknown;
  type?: unknown;
  breakStatus?: unknown;
}>;

const REQUEST_PLACEMENTS: readonly GameAdPlacement[] = [
  'game_session',
  'question_checkpoint',
  'between_rounds',
  'continue_playing',
  'streak_repair',
];

const GOOGLE_BREAK_STATUSES: readonly GoogleBreakStatus[] = [
  'notReady',
  'timeout',
  'invalid',
  'error',
  'noAdPreloaded',
  'frequencyCapped',
  'ignored',
  'other',
  'dismissed',
  'viewed',
];

const FRAME_MESSAGE_TYPES: readonly TestFrameMessageType[] = [
  'ready',
  'prompt',
  'failed',
  'cancel',
  'shown',
  'reward_viewed',
  'reward_dismissed',
  'after_ad',
  'break_done',
];

function elapsedSince(startedAt: number): number {
  return Math.max(0, Math.min(300_000, Math.round(performance.now() - startedAt)));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFrameMessage(value: unknown): value is TestFrameMessage {
  return (
    isRecord(value) &&
    value.channel === TEST_CHANNEL &&
    typeof value.requestId === 'string' &&
    isGameAdUuid(value.requestId) &&
    typeof value.type === 'string' &&
    FRAME_MESSAGE_TYPES.includes(value.type as TestFrameMessageType)
  );
}

function isGoogleBreakStatus(value: unknown): value is GoogleBreakStatus {
  return typeof value === 'string' && GOOGLE_BREAK_STATUSES.includes(value as GoogleBreakStatus);
}

function isSupportedRequest(
  requestId: string,
  format: GameAdSdkRequest['format'],
  placement: GameAdPlacement
): boolean {
  if (!isGameAdUuid(requestId) || !REQUEST_PLACEMENTS.includes(placement)) return false;
  if (format !== 'interstitial' && format !== 'rewarded') return false;

  return format === 'rewarded'
    ? placement === 'continue_playing' || placement === 'streak_repair'
    : placement === 'game_session' ||
        placement === 'question_checkpoint' ||
        placement === 'between_rounds' ||
        placement === 'continue_playing';
}

const testFrameGameAdSdk: GameAdSdk = {
  request({ requestId, format, placement, signal, onShown }) {
    const startedAt = performance.now();
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return Promise.resolve({ status: 'failed', elapsedMs: 0, reason: 'sdk_load_failed' });
    }
    if (signal.aborted) {
      return Promise.resolve({ status: 'cancelled', elapsedMs: elapsedSince(startedAt) });
    }
    if (!isSupportedRequest(requestId, format, placement)) {
      return Promise.resolve({ status: 'failed', elapsedMs: 0, reason: 'sdk_error' });
    }
    if (!document.body) {
      return Promise.resolve({ status: 'failed', elapsedMs: 0, reason: 'sdk_load_failed' });
    }

    const frame = document.createElement('iframe');
    frame.title = 'Test advertisement';
    frame.setAttribute('sandbox', 'allow-scripts');
    frame.setAttribute('allow', 'autoplay');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.position = 'fixed';
    frame.style.inset = '0';
    frame.style.width = '100vw';
    frame.style.height = '100vh';
    frame.style.border = '0';
    frame.style.zIndex = '2147483646';
    frame.style.background = 'transparent';
    frame.style.visibility = 'hidden';
    frame.style.pointerEvents = 'none';

    const frameUrl = new URL(GAME_AD_TEST_FRAME_PATH, window.location.origin);
    frameUrl.searchParams.set('requestId', requestId);
    frameUrl.searchParams.set('format', format);
    frameUrl.searchParams.set('placement', placement);
    frame.src = frameUrl.toString();

    return new Promise<GameAdSdkResult>((resolve) => {
      let settled = false;
      let ready = false;
      let requestSent = false;
      let prompted = false;
      let shown = false;
      let rewardViewed = false;
      let afterAd = false;
      let breakStatus: GoogleBreakStatus | undefined;
      let placementTimer: number | undefined;

      const cleanup = () => {
        if (loadTimer !== undefined) window.clearTimeout(loadTimer);
        if (placementTimer !== undefined) window.clearTimeout(placementTimer);
        window.removeEventListener('message', onMessage);
        signal.removeEventListener('abort', onAbort);
        frame.removeEventListener('error', onFrameError);
        frame.remove();
      };

      const finish = (status: SdkTerminalStatus, reason?: GameAdReason) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve({ status, elapsedMs: elapsedSince(startedAt), ...(reason ? { reason } : {}) });
      };

      const loadTimer = window.setTimeout(
        () => finish('failed', 'sdk_load_failed'),
        FRAME_LOAD_TIMEOUT_MS
      );

      const completeIfClosed = () => {
        if (breakStatus !== 'viewed' || !shown || !afterAd) return;
        if (format === 'rewarded' && !rewardViewed) return;
        finish('completed');
      };

      const finishFromBreakStatus = () => {
        if (breakStatus === undefined) return;
        switch (breakStatus) {
          case 'viewed':
            if (!shown) {
              finish('failed', 'sdk_error');
              return;
            }
            completeIfClosed();
            return;
          case 'dismissed':
            // Google documents adBreakDone as the final placement callback.
            finish('dismissed');
            return;
          case 'timeout':
            finish('timeout', 'sdk_timeout');
            return;
          case 'noAdPreloaded':
          case 'frequencyCapped':
          case 'ignored':
          case 'other':
            finish(shown ? 'failed' : 'no_fill', shown ? 'sdk_error' : 'sdk_no_fill');
            return;
          case 'notReady':
            finish('failed', 'sdk_load_failed');
            return;
          case 'invalid':
          case 'error':
            finish('failed', 'sdk_error');
        }
      };

      const onAbort = () => finish('cancelled', 'user_cancelled');
      const onFrameError = () => finish('failed', requestSent ? 'sdk_error' : 'sdk_load_failed');
      const sendRequest = () => {
        if (settled || signal.aborted || requestSent || !ready) return;
        const target = frame.contentWindow;
        if (!target) {
          finish('failed', 'sdk_load_failed');
          return;
        }

        requestSent = true;
        try {
          // The opaque sandbox has origin "null", so the request must use "*";
          // replies are still bound to its exact WindowProxy and UUID below.
          target.postMessage({ channel: TEST_CHANNEL, type: 'launch', requestId }, '*');
          placementTimer = window.setTimeout(
            () => finish('timeout', 'sdk_timeout'),
            PLACEMENT_TIMEOUT_MS
          );
        } catch {
          finish('failed', 'sdk_load_failed');
        }
      };

      const onMessage = (event: MessageEvent<unknown>) => {
        if (settled || event.source !== frame.contentWindow || event.origin !== 'null') return;
        if (!isFrameMessage(event.data) || event.data.requestId !== requestId) return;

        switch (event.data.type) {
          case 'ready':
            if (ready || requestSent) return;
            ready = true;
            if (loadTimer !== undefined) window.clearTimeout(loadTimer);
            sendRequest();
            return;
          case 'failed':
            finish('failed', requestSent ? 'sdk_error' : 'sdk_load_failed');
            return;
          case 'prompt':
            if (!requestSent || prompted || shown) return;
            prompted = true;
            frame.removeAttribute('aria-hidden');
            frame.style.visibility = 'visible';
            frame.style.pointerEvents = 'auto';
            return;
          case 'cancel':
            if (!requestSent || !prompted) return;
            finish('cancelled', 'user_cancelled');
            return;
          case 'shown':
            if (!requestSent || !prompted || shown) return;
            shown = true;
            frame.removeAttribute('aria-hidden');
            frame.style.visibility = 'visible';
            frame.style.pointerEvents = 'auto';
            try {
              onShown();
            } catch {
              finish('failed', 'sdk_error');
            }
            return;
          case 'reward_viewed':
            if (!requestSent || format !== 'rewarded' || !shown || rewardViewed) return;
            rewardViewed = true;
            completeIfClosed();
            return;
          case 'reward_dismissed':
            if (!requestSent || format !== 'rewarded' || !shown) return;
            // adDismissed is a documented terminal close and never grants reward.
            finish('dismissed');
            return;
          case 'after_ad':
            if (!requestSent || !shown || afterAd) return;
            afterAd = true;
            completeIfClosed();
            return;
          case 'break_done':
            if (!requestSent || !isGoogleBreakStatus(event.data.breakStatus)) return;
            if (breakStatus !== undefined) return;
            breakStatus = event.data.breakStatus;
            finishFromBreakStatus();
        }
      };

      window.addEventListener('message', onMessage);
      signal.addEventListener('abort', onAbort, { once: true });
      frame.addEventListener('error', onFrameError, { once: true });
      try {
        document.body.appendChild(frame);
      } catch {
        finish('failed', 'sdk_load_failed');
      }
    });
  },
};

/** This adapter is mock-only; live Google ad placement must run in the game document itself. */
export const googleTestGameAdSdk = testFrameGameAdSdk;
